import { useState, useEffect, useRef } from 'react'
import { Progress as ProgressPrimitive } from 'radix-ui'
import { cn } from '@/lib/utils'

type Phase = 'idle' | 'approaching' | 'completing' | 'reversing'

export function FetchingProgress(props: { isFetching?: boolean; className?: string }) {
  const isFetching = props.isFetching ?? false
  const [showProgress, setShowProgress] = useState(false)
  const [progress, setProgress] = useState(0)
  const [phase, setPhase] = useState<Phase>('idle')
  const [completionMs, setCompletionMs] = useState(300)
  const indicatorRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const timeoutRef2 = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isVisibleRef = useRef(false)

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    if (timeoutRef2.current) clearTimeout(timeoutRef2.current)

    if (isFetching) {
      // Delay showing the progress bar by 200ms
      timeoutRef.current = setTimeout(() => {
        isVisibleRef.current = true
        setShowProgress(true)
        setProgress(0)
        setPhase('approaching')

        // Start the approach animation with CSS
        requestAnimationFrame(() => {
          setProgress(95)
        })
      }, 200)
    } else {
      // Fast request that never showed the bar
      if (!isVisibleRef.current) return

      // Read current bar position for a proportional completion duration
      let remainingPercent = 5
      if (indicatorRef.current?.parentElement) {
        const parentRect = indicatorRef.current.parentElement.getBoundingClientRect()
        const elRect = indicatorRef.current.getBoundingClientRect()
        if (parentRect.width > 0) {
          const currentPercent = ((elRect.right - parentRect.left) / parentRect.width) * 100
          remainingPercent = Math.max(0, 100 - currentPercent)
        }
      }

      // Scale duration proportionally: longer for more remaining distance
      const duration = Math.max(150, Math.min(400, remainingPercent * 6))
      setCompletionMs(duration)
      setPhase('completing')
      setProgress(100)

      // Then reverse to 0 from the right after the completion animation
      timeoutRef.current = setTimeout(() => {
        setPhase('reversing')
        setProgress(0)

        // Hide after the reverse animation completes
        timeoutRef2.current = setTimeout(() => {
          setShowProgress(false)
          setPhase('idle')
          isVisibleRef.current = false
        }, 600)
      }, duration + 50)
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      if (timeoutRef2.current) clearTimeout(timeoutRef2.current)
    }
  }, [isFetching])

  return (
    <div className={cn('pointer-events-none sticky top-0 right-0 left-0 z-10', props.className)}>
      <ProgressPrimitive.Root
        data-slot="progress"
        className={cn('bg-transparent h-0.5 w-full overflow-hidden rounded-none', !showProgress && 'invisible')}
      >
        <ProgressPrimitive.Indicator
          ref={indicatorRef}
          data-slot="progress-indicator"
          className={cn(
            'bg-primary h-full flex-1 w-full',
            phase === 'reversing'
              ? 'transition-[width] duration-600 ease-in-out'
              : phase === 'approaching'
                ? 'transition-transform duration-[2s] ease-[cubic-bezier(0.19,1,0.22,1)]'
                : '',
          )}
          style={
            phase === 'reversing'
              ? {
                  width: `${progress || 0}%`,
                  marginLeft: 'auto',
                }
              : {
                  // In 'completing' phase, use a dynamic duration with ease-in-out
                  // to match the decelerating approach velocity
                  ...(phase === 'completing' ? { transition: `transform ${completionMs}ms ease-in-out` } : {}),
                  transform: `translateX(-${100 - (progress || 0)}%)`,
                  transformOrigin: 'left',
                }
          }
        />
      </ProgressPrimitive.Root>
    </div>
  )
}
